import {
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  approveResell,
  getResellFactoringMissingApprovals,
  rejectResell,
} from 'lib/api/reSell/factoring';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { useFactoringResell } from '../../../../context';

interface MissingApproval {
  id: number;
  name: string;
  approved: boolean;
  approvedCountRequired: number;
  namesThatApprovedThisRole: string;
  userCanApprove: boolean;
}

interface Comitee {
  name: string;
}

interface UserRole {
  id: number;
  name: string;
}

const FactoringResellMissingApprovalContainer: React.FC = () => {
  const { resell, setOpenNewResell, fetchResellings, PAGE_SIZE } =
    useFactoringResell();
  const [missingApprovals, setMissingApprovals] = useState<MissingApproval[]>(
    [],
  );
  const [comitee, setComitee] = useState<Comitee | null>(null);
  const [loading, setLoading] = useState(true);
  const [canApprove, setCanApprove] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedUserRoleId, setSelectedUserRoleId] = useState<number | ''>('');
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();

  const resellId = resell ? ('id' in resell ? resell.id : null) : null;

  const setUp = async () => {
    await fetchResellings({ page: 0, pageSize: PAGE_SIZE });
  };

  const setUpApprovals = async () => {
    if (!resellId) return;
    setLoading(true);
    try {
      const response = await getResellFactoringMissingApprovals(
        dispatch,
        resellId,
      );
      setMissingApprovals(response.rolesToApprove);
      setComitee(response.comitee);
      const userRolesList = Array.isArray(user?.roles) ? user.roles : [];
      const requiredRoleIds = new Set(
        response.rolesToApprove
          .filter((r: MissingApproval) => !r.approved)
          .map((r: MissingApproval) => r.id),
      );
      const approvedRoleIds = new Set(
        response.approvals
          .filter((a: { userId: number }) => a.userId === user?.id)
          .map((a: { roleId: number }) => a.roleId),
      );
      const userRolesWithoutApproval = userRolesList.filter(
        (userRole: UserRole) =>
          requiredRoleIds.has(userRole.id) && !approvedRoleIds.has(userRole.id),
      );
      const canApproveResult =
        userRolesWithoutApproval.length > 0 &&
        response.rolesToApprove.some(
          (entry: MissingApproval) => entry.userCanApprove,
        );
      setCanApprove(canApproveResult);
      setUserRoles(userRolesWithoutApproval);
      if (userRolesWithoutApproval.length === 1) {
        setSelectedUserRoleId(userRolesWithoutApproval[0].id);
      }
    } catch (error) {
      alert('Error al cargar las aprobaciones faltantes');
    }
    setLoading(false);
  };

  const handleApprove = async () => {
    if (!resellId) return;
    setLoading(true);
    try {
      await approveResell(dispatch, resellId, {
        reason,
        roleId: selectedUserRoleId,
      });
      setOpenNewResell(false);
      await setUpApprovals();
      await setUp();
      showAlert(dispatch, 'success', 'Solicitud aprobada');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al aprobar la solicitud');
    }
  };

  const handleReject = async () => {
    if (!resellId) return;
    setLoading(true);
    try {
      await rejectResell(dispatch, resellId, {
        reason,
        roleId: selectedUserRoleId,
      });
      await setUpApprovals();
      await setUp();
      setOpenNewResell(false);
      showAlert(dispatch, 'success', 'Solicitud rechazada');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al rechazar la solicitud');
    }
  };

  useEffect(() => {
    void setUpApprovals();
  }, []);

  return (
    <div>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <div>
          <div style={{ padding: '10px 0px' }}>
            <Typography variant="body1">
              <b>
                {comitee
                  ? `Aprobaciones necesarias por ${comitee.name}`
                  : '(Comité pendiente)'}
              </b>
            </Typography>
            <Typography variant="body1">
              {comitee
                ? 'Estas son las aprobaciones requeridas para la revisión de la solicitud.'
                : ' Debes esperar a que se le asigne un comité para poder revisar esta solicitud.'}
            </Typography>
          </div>
          {comitee && (
            <EnhancedTable
              data={missingApprovals}
              dataHeaders={[
                { key: 'name', label: 'Nombre' },
                { key: 'approvedCountRequired', label: 'Requeridas' },
                {
                  key: 'approved',
                  label: 'Revisado',
                  type: 'vission',
                },
                {
                  key: 'namesThatApprovedThisRole',
                  label: 'Aprobado por',
                },
              ]}
              rowsPerPageDefault={5}
            />
          )}
          {canApprove && (
            <div style={{ padding: '10px 0px' }}>
              <Typography variant="body1">
                <b>Observaciones</b>
              </Typography>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <TextField
                  label="Por qué está tomando esta decisión?"
                  fullWidth
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  style={{ flex: 1 }}
                />
                {userRoles && (
                  <FormControl
                    margin="normal"
                    style={{
                      minWidth: 200,
                      alignSelf: 'stretch',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <InputLabel id="firmas-como-label">Firmas como</InputLabel>
                    <Select
                      labelId="firmas-como-label"
                      id="firmas-como-select"
                      value={selectedUserRoleId}
                      label="Firmas como"
                      onChange={(e) =>
                        setSelectedUserRoleId(e.target.value as number)
                      }
                    >
                      {userRoles.map((role: UserRole) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>
              <DialogActions>
                <CustomButton
                  onClick={() => setOpenNewResell(false)}
                  color="secondary"
                >
                  Salir
                </CustomButton>
                <CustomButton
                  onClick={() => handleApprove()}
                  color="success"
                  disabled={!reason}
                >
                  Aprobar
                </CustomButton>
                <CustomButton
                  onClick={() => handleReject()}
                  color="error"
                  disabled={!reason}
                >
                  Rechazar
                </CustomButton>
              </DialogActions>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FactoringResellMissingApprovalContainer;
